import { TranslateOptions } from 'vue-i18n';

import { currentLocale, i18n } from '@/plugins/i18n';
import { useCommunityStore } from '@/store/communityStore';
import type { StoredFile } from '@/store/common.interface';
import type { ProjectModuleConfigCTAButtonDto, ProjectModuleHeroConfigDto } from '@/store/module.interface';
import type { Project } from '@/store/project.interface';
import { LanguageTranslation, Locales } from '@/store/language.interface';
import { getImageUrl } from '@/utils/image.helper';

export function formatMoney(value: number, languageEnabled: Locales[]): string {
  return Intl.NumberFormat(`${getLocale(languageEnabled)}-CA`, {
    currency: 'CAD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(value);
}

export function getLabel(
  key: string,
  languageEnabled: Locales[] | undefined = [],
  options: TranslateOptions<Locales> = {},
): string {
  options.locale = getLocale(languageEnabled);
  return i18n.global.t(key, key, options);
}

export function getLanguageName(
  languageEnabled: Locales[] | undefined = [],
  shouldBeLowerCase: boolean = false,
): string {
  const locale = getLocale(languageEnabled);
  const key = `languages.${locale}`;
  const name = i18n.global.t(key, key, { locale: currentLocale() });
  return shouldBeLowerCase ? name.toLowerCase() : name;
}

export function getLocale(languageEnabled: Locales[]): Locales {
  const locale = currentLocale();
  const community = useCommunityStore().community;
  if (languageEnabled.length === 0 && community.languageEnabled) {
    languageEnabled.push(...community.languageEnabled);
  }
  const defaultLanguage = community.defaultLanguage;
  // Prioritize the community's default language if available
  const defaultLocale = languageEnabled.includes(defaultLanguage) ? defaultLanguage : languageEnabled[0];
  // Select the locale based on the current user's locale if available or use the default locale
  return languageEnabled.includes(locale) ? locale : defaultLocale;
}

export function getTranslation(
  field: object,
  key: string,
  languageEnabled: Locales[] | undefined = [],
): string | undefined | null {
  if (!field?.[key]) return null;
  const locale = getLocale(languageEnabled);
  const fallbackLocales = languageEnabled.filter((x) => x != locale && field?.[key]?.[x]);
  return field?.[key]?.[locale] || field?.[key]?.[fallbackLocales?.[0]];
}

export function isHtmlEmpty(html: string): boolean {
  if (!html) return true;
  const element = document.createElement('div');
  element.innerHTML = html;
  return element.innerText?.trim() === '';
}

export function isOverflown(text: string, count: number): boolean {
  return text?.length > count;
}

// What a Hero reads of its block, whether a project's module or a page's block. Its config is read as a Hero's.
export type HeroBlock = { title?: LanguageTranslation | null; config: object };

// With no project, as on a page, `useProjectTitle` and `useProjectImage` give the block's own title and the community
// banner.
export function getHeroDisplayData(
  module: HeroBlock,
  project: Project | null | undefined,
  communityBanner: StoredFile | null | undefined,
  languageEnabled: Locales[] | undefined,
): {
  title: string | null | undefined;
  description: string | null | undefined;
  imageUrl: string;
} {
  const moduleConfig = module.config as ProjectModuleHeroConfigDto;
  const useProjectContent = moduleConfig?.useProjectTitle && !!project;
  const title = useProjectContent
    ? getTranslation(project as Project, 'title', languageEnabled)
    : getTranslation(module, 'title', languageEnabled);
  const description = useProjectContent
    ? getTranslation(project as Project, 'description', languageEnabled)
    : getTranslation(moduleConfig?.overlay, 'description', languageEnabled);
  const imageUrl = moduleConfig?.useProjectImage
    ? (getImageUrl(project?.bannerFile) ?? getImageUrl(communityBanner))
    : getImageUrl(moduleConfig?.image);
  return { title, description, imageUrl };
}

// On a page, a Hero draws only its link buttons: a button that scrolls to one of the project's modules has nowhere to
// go, so it is disabled rather than drawn inert.
export function getPageHeroCtaButtons<T extends Record<string, ProjectModuleConfigCTAButtonDto | undefined>>(
  ctaButtons: T | undefined,
): T | undefined {
  if (!ctaButtons) return ctaButtons;
  return Object.fromEntries(
    Object.entries(ctaButtons).map(([name, button]) => [
      name,
      button?.linkType === 'url' ? button : button && { ...button, enabled: false },
    ]),
  ) as T;
}
